import React, { useState } from "react";
import socketIOClient from "socket.io-client";
import styled, { css } from "styled-components";
import { useSocketListener } from "./Hooks/useSocketListener";
import { SocketContext, PlayersContext } from "./Components/Common/Contexts";
import Player from "./Types/Player";
import StartScreen from "./Components/StartScreen";
import Game from "./Types/Game";
import GameView from "./Components/GameView";
import { DisconnectedPlayerPopup } from "./Components/Common/Popup";
import PlayerName from "./Components/PlayerName";
import { Translation } from "./Hooks/useTranslationContext";
import Flags from "./Components/Flags";

const socket = socketIOClient({ path: window.REACT_APP_SOCKET_URL });

const SocketId = styled.div<{ isMySocketId: boolean }>`
  display: flex;
  ${({ isMySocketId }) =>
    isMySocketId &&
    css`
      font-weight: bold;
    `}
`;

const Container = styled.div`
  margin: 1em 1em;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

function App() {
  const [sockets, setSockets] = useState<{ [socketId: string]: Player }>({});
  const [activeGameId, setActiveGameId] = useState<number>();
  const [games, setGames] = useState<{ [gameId: number]: Game }>({});
  const [showDisconnect, setShowDisconnect] = useState(false);
  useSocketListener("error", socket, (errorLink: string) => {
    window.location.href = errorLink;
  });
  useSocketListener(
    "gamesUpdate",
    socket,
    (gamesDict: { [gameId: number]: Game }) => {
      setGames(gamesDict);
    }
  );
  useSocketListener(
    "socketUpdate",
    socket,
    (socketsDict: { [socketId: string]: Player }) => {
      setSockets(socketsDict);
    }
  );
  useSocketListener("joinGame", socket, (gameId) => {
    setActiveGameId(gameId);
  });
  useSocketListener("disconnect", socket, (reason) => {
    console.log("DISCONNECTED reason: ", reason);
    setShowDisconnect(true);
  });
  return (
    <SocketContext.Provider value={socket}>
      <Translation>
        <PlayersContext.Provider
          value={{
            players: sockets,
            getSocketName: (socketId: string) => sockets[socketId]?.name,
          }}
        >
          <Flags />
          {(!socket.connected ||
            !sockets[socket.id].isConnected ||
            showDisconnect) && <DisconnectedPlayerPopup />}
          {process.env.NODE_ENV === "development" && (
            <>
              Socket ids:
              {Object.entries(sockets).map(([socketId, player]) => (
                <SocketId key={socketId} isMySocketId={socketId === socket.id}>
                  <PlayerName
                    name={player.name ?? "Name missing"}
                    isConnected={player.isConnected}
                    showIcons
                  />
                  {`id: ${socketId}, game: ${player.gameId}, score: ${player.score}, totalScore: ${player.totalScore}`}
                </SocketId>
              ))}
            </>
          )}
          <Container>
            {activeGameId === undefined ? (
              <>
                <StartScreen games={games} />
              </>
            ) : (
              <GameView game={games[activeGameId]} gameId={activeGameId} />
            )}
          </Container>
        </PlayersContext.Provider>
      </Translation>
    </SocketContext.Provider>
  );
}

export default App;
